import { getSessionGeneration, onSessionGenerationChange } from '../../lib/sessionGeneration.js'

export const emptyAccountFeed = () => ({ data: [], status: 'loading', error: '', source: null, missing: [], issues: [], scope: null, coverage: null })
// Selection keys contain only view filters and the local numeric session epoch.
export const accountFeedForRequest = (state, requestKey) => state?.requestKey === requestKey ? state : emptyAccountFeed()
const errorBody = error => error?.body || {}
const safeCount = value => Number.isInteger(value) && value >= 0 ? value : null

export function accountFeedCoverage(response, source, returnedRows) {
  const scope = response?.scope, mapping = scope?.mappingCoverage
  const orderSource = ['clickup-orders', 'clickup-tasks', 'journey-lane'].includes(source.key)
  const typedOrders = orderSource && scope?.entity === 'orders' && scope?.basis === 'clickup.current_assignee_id'
  const companyScope = source.key === 'hubspot-deals' && scope?.basis === 'company.sr_assigned_rep'
  // A legacy unscoped answer must not be accepted as a rep's current book.
  if (!response?.ok || !mapping || !['own', 'all'].includes(scope?.mode)
      || (scope.mode === 'own' && (scope.scoped !== true || (!typedOrders && !companyScope) || !scope.repId))) {
    throw new Error('The order feed did not return current server account scope. No rows are shown.')
  }
  const partial = !['complete', 'empty_upstream'].includes(mapping.status)
    || mapping.upstreamIncomplete === true || scope.incomplete === true || Boolean(response.nextAfter)
  return {
    mode: scope.mode, entity: orderSource ? 'orders' : 'deals', typedOrders, status: mapping.status, partial, returnedRows,
    // HubSpot is a bounded mirror, not proof of a complete ClickUp order book.
    fullOrderBook: source.key.startsWith('clickup-') && !partial && mapping.representativeViewReady === true,
    mappedRows: safeCount(mapping.mappedRows ?? mapping.authorizedRows),
    heldRows: scope.mode === 'all' ? safeCount(mapping.heldRows) : null,
    heldReason: mapping.heldReason || '', note: mapping.note || '',
    nextPage: Boolean(response.nextAfter), representativeViewReady: mapping.representativeViewReady === true,
  }
}

export async function readAccountFeed(sources, signal) {
  const issues = [], missing = []
  let skipClickUp = false
  for (const source of sources) {
    if (signal?.aborted) return null
    if (skipClickUp && source.key.startsWith('clickup-')) continue
    try {
      const response = await source.call({ signal, ...(source.key === 'hubspot-deals' ? {query: {max: 1000}} : {}) })
      if (signal?.aborted) return null
      let data = source.normalize(response)
      if (!Array.isArray(data)) throw new Error('The source did not return a valid row list. Counts are unavailable.')
      const coverage = accountFeedCoverage(response, source, data.length)
      if (coverage.entity === 'orders') data = data.map(row => ({...row, journeyScope: {entity: 'orders', basis: response.scope.basis}}))
      return { data, status: 'done', error: '', source, missing, issues, scope: response.scope, coverage, payload: response }
    } catch (error) {
      if (signal?.aborted || error?.name === 'AbortError') return null
      const body = errorBody(error)
      const issue = { path: source.path, status: error?.status || null, code: body.error || '', detail: body.detail || error.message || 'Read unavailable.' }
      if (error?.status === 404) { missing.push(source); issues.push(issue); continue }
      // Only this explicit source mapping gap permits an independent mirror read.
      // 401/403 and authority outages never fall through to another source.
      if (error?.status === 503 && body.error === 'journey_account_mapping_unavailable' && source.key.startsWith('clickup-')) {
        issues.push(issue); skipClickUp = true; continue
      }
      return { ...emptyAccountFeed(), status: 'error', error: issue.detail, issues: [...issues, issue], missing }
    }
  }
  return { ...emptyAccountFeed(), status: 'unavailable', error: 'Order feed unavailable, not empty.', missing, issues }
}

export function createAccountFeedController({ sources, onChange, readGeneration = getSessionGeneration, subscribe = onSessionGenerationChange }) {
  let state = emptyAccountFeed(), pending = null, abort = null, disposed = false, run = 0
  const emit = next => { state = next; onChange?.(state) }
  const invalidate = () => { run++; abort?.abort(); pending = null; emit(emptyAccountFeed()) }
  const unsubscribe = subscribe(invalidate)
  return {
    getState: () => state,
    load() {
      if (disposed) return Promise.resolve(null)
      if (pending) return pending
      const session = readGeneration(), id = ++run
      abort = new AbortController()
      const current = () => !disposed && id === run && session === readGeneration()
      emit(emptyAccountFeed())
      pending = readAccountFeed(sources, abort.signal).then(result => {
        if (current() && result) emit(result)
        return current() ? result : null
      }).finally(() => { if (current()) pending = null })
      return pending
    },
    dispose() { disposed = true; run++; abort?.abort(); pending = null; unsubscribe() },
  }
}
